import { countOf } from '../../domain/labels.js';
import { COPY_FROM_PARAM, STUDIO_PARAM } from '../../domain/routes.js';
import { setParams } from '../../domain/urlParams.js';
import { useUrlFilter } from '../../hooks/useUrlFilter.js';

/**
 * The Games page's studio filter, which lives in the URL as `?studio=<id>` so a filtered page
 * can be linked to (a studio's Games count, a game's Studio cell, the editor's crumb). Search,
 * segment and status live beside it (`domain/rowFilter.ts`); clearing those keeps this.
 */

/** The studio the page is filtered to, or null. A blank value is no filter. */
export const readStudio = (params: URLSearchParams): string | null => {
    const value = params.get(STUDIO_PARAM)?.trim();
    return value ? value : null;
};

/** The game key a new game is copied from (`newGameRoute`'s `copyFrom`), or null. */
export const readCopyFrom = (params: URLSearchParams): string | null => {
    const value = params.get(COPY_FROM_PARAM)?.trim();
    return value ? value : null;
};

/** The same query string with the studio set, or removed when null. Every other param is kept. */
export const writeStudio = (params: URLSearchParams, studio: string | null): URLSearchParams =>
    setParams(params, { [STUDIO_PARAM]: studio });

/**
 * The Games page's `?studio=` filter, over the shared `useUrlFilter` (wave 3). One scalar, not a
 * patchable object: `patch` replaces it outright, which is exactly what `writeStudio` already
 * does — `Partial<string | null>` is `string | null`, so it takes the next value as is.
 */
export const useStudioFilter = () => {
    const { filters: studio, patch: setStudio } = useUrlFilter<string | null>(readStudio, writeStudio, (params) =>
        writeStudio(params, null),
    );
    return { studio, setStudio };
};

/** The fact line: `312 games`, or `Studio One · 41 games` when filtered to a studio. */
export const gamesFact = (total: number | null, studioLabel: string | null): string | undefined => {
    const count = total === null ? null : countOf('game', total);
    if (studioLabel) return count ? `${studioLabel} · ${count}` : studioLabel;
    return count ?? undefined;
};

/** The fact line: `312 studios · 1,204 games`, dropping either half whose count is zero or unknown. */
export const studiosFact = (totalStudios: number | null, totalGames: number | null): string | undefined => {
    const parts = [
        totalStudios === null ? null : countOf('studio', totalStudios),
        totalGames ? countOf('game', totalGames) : null,
    ].filter((part): part is string => Boolean(part));
    return parts.length ? parts.join(' · ') : undefined;
};
